import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../context/ContextProvider";
import Feedback from "../../shared/Feedback";
import Support from "../../shared/Support";

const SideNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, collapseSideBar, setCollapseSideBar, setNestedHeader } =
    useContextState();
  const [hasNotificationBar, setHasNotificationBar] = useState(false);
  const { setOrgFolderContentsAccountsPage } = useContextState()

  // Navigation items configuration
  const navItems = [
    // {
    //   to: "/",
    //   icon: "/assets/icons/dashboard.svg",
    //   label: "Dashboard",
    //   onClick: () => setNestedHeader(null),
    // },
    {
      to: "/organizations",
      icon: "/assets/icons/organization.svg",
      label: "Organizations",
      tourClass: "org-tour-nav-organizations",
      onClick: () => {
		setOrgFolderContentsAccountsPage(null)
        if (location.pathname === "/organizations") {
          navigate("/organizations?reset=" + new Date().getTime());
        } else {
          navigate("/organizations");
        }
      },
    },
    {
      to: "/monthly-report",
      icon: "/assets/icons/dashboard.svg",
      label: "Monthly Report",
      tourClass: "org-tour-nav-monthly-report",
      onClick: () => {
        setNestedHeader(null);
        setOrgFolderContentsAccountsPage(null);
      },
    },
    {
      to: "/reseller/customers",
      icon: "/assets/icons/user.svg",
      label: "My Customers",
      show: role === "reseller",
      tourClass: "org-tour-nav-my-customers",
      onClick: () => {
        setNestedHeader(null);
        setOrgFolderContentsAccountsPage(null);
      },
    },

    {
      to: "/audit-log",
      icon: "/assets/icons/audit.svg",
      label: "Audit Log",
      tourClass: "org-tour-nav-audit-log",
      onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  },
    },
    {
      to: "/archive",
      icon: "/assets/icons/archived.svg",
      label: "Archived Files",
      tourClass: "org-tour-nav-archive",
      onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  },
    },
    {
      to: "/payment-methods",
      icon: "/assets/icons/payment.svg",
      label: "Payment Methods",
      tourClass: "org-tour-nav-payment-methods",
      onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  },
    },
    {
      href: "https://wowbackupandrestore.com/wow-continuity",
      icon: "/assets/icons/file.svg",
      label: "WOW Continuity",
      external: true,
      onClick: () => {
        setNestedHeader(null);
        setOrgFolderContentsAccountsPage(null);
      },
    },
    {
      to: "/users",
      icon: "/assets/icons/user.svg",
      label: "Users",
      show: role == "superadmin",
	  onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  }
    },
    {
      to: "/resellers",
      icon: "/assets/icons/user.svg",
      label: "Resellers",
      show: role == "superadmin",
	  onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  }
    },
    {
      to: "/settings",
      icon: "/assets/icons/settings.svg",
      label: "Settings",
      tourClass: "org-tour-nav-settings",
      divider: true,
	  onClick: () => {
		setNestedHeader(null)
		setOrgFolderContentsAccountsPage(null)
	  }
    },
  ];

  useEffect(() => {
    // Auto-collapse sidebar on small screens
    if (window.innerWidth < 500) {
      setCollapseSideBar(true);
    }
  }, [setCollapseSideBar]);

  const navWidth = collapseSideBar ? "-left-64 w-20" : "left-0 sm:w-64 w-56";
  const navPosition = hasNotificationBar ? "top-10" : "top-0";
  const navHeight = hasNotificationBar ? "h-[calc(100vh-2.5rem)]" : "h-[100vh]";

  return (
    <div
      className={`app-tour-sidebar ${navWidth} ${navPosition} ${navHeight} bg-brand-black fixed sm:left-0 flex flex-col justify-between z-30`}
    >
      {/* Toggle button */}
      <button
        onClick={() => setCollapseSideBar(!collapseSideBar)}
        className="absolute hidden sm:flex w-9 h-9 rounded-full shadow-lg items-center justify-center -right-5 top-11 bg-white z-40"
      >
        <ReactSVG src="/assets/icons/side-toggle.svg" />
      </button>

      <div className="flex justify-center flex-col">
        <div className="flex justify-center">
          <img
            src={`/assets/logo/${
              collapseSideBar ? "short-logo.png" : "logo.png"
            }`}
            className={`${collapseSideBar ? "w-12 mt-4" : "w-44"}`}
            alt="logo"
          />
        </div>

        {/* Navigation items */}
        <div className="p-4 mt-10">
          {navItems.map((item, index) =>
            item.show === false ? null : (
              <div key={index}>
                {item.divider && (
                  <div className="px-6 my-5 border-t border-gray-700 w-100" />
                )}
                {item.href ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={item.onClick}
                    className={`flex mb-1 group items-center gap-3 px-3 p-2 rounded-lg hover:bg-blue-500 hover:text-white text-gray-400 cursor-pointer ${
                      item.tourClass || ""
                    }`}
                  >
                    <ReactSVG src={item.icon} />
                    {!collapseSideBar && (
                      <span className="flex items-center gap-1">
                        {item.label}
                        {item.external && (
                          <span className="text-[10px] opacity-70" aria-hidden>
                            ↗
                          </span>
                        )}
                      </span>
                    )}
                  </a>
                ) : (
                  <NavLink
                    to={item.to}
                    onClick={item.onClick}
                    className={({ isActive }) =>
                      `flex mb-1 group items-center gap-3 px-3 p-2 ${
                        isActive ? "bg-blue-500 text-white" : ""
                      } rounded-lg hover:bg-blue-500 hover:text-white text-gray-400 cursor-pointer ${
                        item.tourClass || ""
                      }`
                    }
                  >
                    <ReactSVG src={item.icon} />
                    {!collapseSideBar && item.label}
                  </NavLink>
                )}
              </div>
            )
          )}
          <Feedback />
		  {/*<Support />*/}
        </div>
      </div>

      {/* Copyright footer */}
      {!collapseSideBar && (
        <div className="text-center text-gray-400 p-4 text-xs">
          Copyright © 2020-{new Date().getFullYear()} All rights reserved.
        </div>
      )}
    </div>
  );
};

export default SideNav;
